import * as React from 'react';
import { useFieldArray, useFormContext, useWatch } from 'react-hook-form';

import { TempControlFacilityType } from '__generated-api__';
import { Button } from 'components/Button';
import FixtureSplitGroup from 'tempcontrol/components/fixture-split-group';
import {
  facilityUsesFlowRate,
  fixtureRowsForIndices,
  fixtureTypeLabels,
  primaryFixtureIndices,
} from 'tempcontrol/enums';
import { WizardFormValues } from 'tempcontrol/validations/schemas';

interface Step4FixturesProps {
  isReadOnly?: boolean;
  onNext?: () => void;
  isSubmitting?: boolean;
  nextLabel?: string;
}

const primaryFixtureSet = new Set(primaryFixtureIndices);
const advancedFixtureIndices = fixtureTypeLabels
  .map((fixture) => fixture.index)
  .filter((index) => !primaryFixtureSet.has(index));

/**
 * Step 3 of 3 - the user enters fixture quantities served by this valve.
 */
export const Step4Fixtures: React.VFC<Step4FixturesProps> = ({
  isReadOnly,
  onNext,
  isSubmitting,
  nextLabel = 'Get Results',
}) => {
  const { control } = useFormContext<WizardFormValues>();
  useFieldArray({ control, name: 'fixtures' });
  const [showAdvanced, setShowAdvanced] = React.useState(false);
  const facility = useWatch<WizardFormValues>({ name: 'facility' }) as TempControlFacilityType | undefined;
  const withFlowRate = facilityUsesFlowRate(facility);

  const primaryRows = React.useMemo(() => fixtureRowsForIndices(primaryFixtureIndices), []);
  const advancedRows = React.useMemo(() => fixtureRowsForIndices(advancedFixtureIndices), []);

  return (
    <>
      <h5 className="u-mb-spacer-base">Fixture Totals</h5>
      {withFlowRate && (
        <p className="c-note u-mb-spacer-base">
          This facility type sizes on maximum simultaneous use, so enter the flow rate (GPM) for each fixture you add
          alongside its quantity. Peak demand is the sum of quantity × flow rate.
        </p>
      )}
      <fieldset disabled={isReadOnly} className="u-mb-0">
        <div className="o-row o-row--small-gutters">
          <FixtureSplitGroup
            rows={primaryRows}
            advancedRows={advancedRows}
            showAdvanced={showAdvanced}
            onShowAdvancedChange={setShowAdvanced}
            disabled={isReadOnly}
            withFlowRate={withFlowRate}
          />
          {typeof onNext === 'function' && (
            <div className="o-col-12@sm">
              <Button variant="primary" onClick={onNext} isLoading={isSubmitting} rightIcon="arrow">
                {nextLabel}
              </Button>
            </div>
          )}
        </div>
      </fieldset>
    </>
  );
};

export default Step4Fixtures;
